"use client"

import { useState } from "react"
import { PencilLine, X } from "lucide-react"

const FIELD_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "What's wrong? (optional)" },
  { value: "costType", label: "Cost / funding (free, fee, stipend)" },
  { value: "deadline", label: "Deadline / dates" },
  { value: "website", label: "Website link" },
  { value: "applicationUrl", label: "Application link" },
  { value: "description", label: "Description / details" },
  { value: "location", label: "Location" },
  { value: "closed", label: "Program has closed / is inactive" },
  { value: "other", label: "Something else" },
]

export function SuggestCorrectionButton({ residencyId }: { residencyId: string }) {
  const [open, setOpen] = useState(false)
  const [field, setField] = useState("")
  const [message, setMessage] = useState("")
  const [email, setEmail] = useState("")
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle")
  const [errorMsg, setErrorMsg] = useState("")

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (message.trim().length < 4) {
      setErrorMsg("Please describe what's wrong.")
      setState("error")
      return
    }
    setState("sending")
    setErrorMsg("")
    try {
      const res = await fetch("/api/corrections", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          residencyId,
          field: field || undefined,
          message: message.trim(),
          email: email.trim() || undefined,
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || "Something went wrong")
      }
      setState("sent")
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong")
      setState("error")
    }
  }

  if (state === "sent") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-[var(--accent)]">
        <PencilLine size={14} />
        Thanks — we&apos;ll review it.
      </span>
    )
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs text-[var(--muted-foreground)] hover:text-[var(--primary)] transition-colors"
        title="See something wrong? Suggest a correction"
      >
        <PencilLine size={14} />
        <span>Suggest a correction</span>
      </button>
    )
  }

  return (
    <div className="w-full max-w-md border border-[var(--border)] rounded-xl p-4 bg-[var(--card)] text-left">
      <div className="flex items-center justify-between mb-3">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium">
          <PencilLine size={15} />
          Suggest a correction
        </span>
        <button
          onClick={() => {
            setOpen(false)
            setState("idle")
            setErrorMsg("")
          }}
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <select
          value={field}
          onChange={(e) => setField(e.target.value)}
          className="w-full text-sm rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2"
        >
          {FIELD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
          maxLength={2000}
          rows={4}
          placeholder="What's incorrect, and what should it say? A source link helps."
          className="w-full text-sm rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2"
        />

        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email (optional — if we need to follow up)"
          className="w-full text-sm rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2"
        />

        {state === "error" && (
          <p className="text-xs text-red-600">{errorMsg}</p>
        )}

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={state === "sending"}
            className="text-sm font-medium rounded-lg bg-[var(--primary)] text-[var(--primary-foreground)] px-4 py-2 hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {state === "sending" ? "Sending…" : "Submit"}
          </button>
          <span className="text-xs text-[var(--muted-foreground)]">
            Goes to our team — no account needed.
          </span>
        </div>
      </form>
    </div>
  )
}
